import { Component, inject, OnInit } from '@angular/core';
import { NotificationCenterStore } from '../../store/notification-center.store';

import { CardModule } from 'primeng/card';
import { ButtonModule, ModalRef, SelectItem, SelectModule, SpinnerModule, ToggleModule } from '@eg/ui';
import { CustomerRemarkModel, CustomerRemarkTypeModel, CustomerRemarkSearchParamModel, RemarkStatusTypeModel } from '@railmybox/api-user';
import { NotificationItemComponent } from '../../presentation/notification-item/notification-item.component';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { Router } from '@angular/router';

@Component({
  selector: 'rmb-notification-center-details',
  imports: [CardModule, ButtonModule, ToggleModule, SelectModule, NotificationItemComponent, TranslocoModule, SpinnerModule],
  templateUrl: './notification-center-details.component.html',
  styleUrl: './notification-center-details.component.scss',
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: [{ scope: 'notification-center', alias: 'notification-center' }],
    },
  ],
})
export class NotificationCenterDetailsComponent implements OnInit {
  readonly TRANSLOCO_PREFIX = 'notification-center';
  readonly notificationCenterStore = inject(NotificationCenterStore);
  private readonly router = inject(Router);
  private modalRef = inject(ModalRef<NotificationCenterDetailsComponent>);

  isShowUnreadOnly = false;
  readonly filterOptions: SelectItem[] = [
    { label: `${this.TRANSLOCO_PREFIX}.all`, value: 'all' },
    { label: `${this.TRANSLOCO_PREFIX}.tasks`, value: 'tasks' },
  ];
  filterValue = 'all';

  notificationFilter: CustomerRemarkSearchParamModel;

  ngOnInit() {
    this.notificationCenterStore.fetchNotifications(this.notificationFilter);
  }

  handleRead(notification: CustomerRemarkModel) {
    if (!notification.read) {
      this.notificationCenterStore.markAsRead(notification.id);
    } else {
      this.notificationCenterStore.markAsNew(notification.id);
    }
  }

  handleTask(notification: CustomerRemarkModel) {
    if (!notification.task) {
      this.notificationCenterStore.markAsTask(notification.id);
    } else {
      this.notificationCenterStore.markAsNotTask(notification.id);
    }
  }

  onShowUnreadChanged(showUnread: boolean) {
    this.isShowUnreadOnly = showUnread;

    if (this.isShowUnreadOnly) {
      this.notificationFilter = { ...this.notificationFilter, status: RemarkStatusTypeModel.New };
    } else {
      this.notificationFilter = { ...this.notificationFilter, status: undefined };
    }

    this.notificationCenterStore.fetchNotifications(this.notificationFilter);
  }

  onFilterChange(value: string) {
    this.filterValue = value;

    if (this.filterValue === 'tasks') {
      this.notificationFilter = { ...this.notificationFilter, type: CustomerRemarkTypeModel.Task };
    } else {
      this.notificationFilter = { ...this.notificationFilter, type: undefined };
    }

    this.notificationCenterStore.fetchNotifications(this.notificationFilter);
  }

  async handleClickNotification(event: PointerEvent) {
    const target = event.target;
    if (target instanceof HTMLImageElement && target.currentSrc?.startsWith('data:image/')) {
      const blob = this.dataUrlToBlob(target.currentSrc);
      const objectUrl = URL.createObjectURL(blob);

      window.open(objectUrl, '_blank');

      return;
    }

    const el = target as Element | null;
    if (!el) return;

    const holder = el.closest<HTMLElement>('[data-link-path]');
    if (holder) {
      await this.router.navigate([holder.getAttribute('data-link-path')], {
        replaceUrl: true,
      });
      this.modalRef?.close();
    }
  }

  private dataUrlToBlob(dataUrl: string): Blob {
    const [meta, base64Data] = dataUrl.split(',');
    const mimeMatch = meta.match(/data:(.*?);base64/);
    const mimeType = mimeMatch?.[1] ?? 'application/octet-stream';
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  }
}
